import { useState } from "react";
import { motion } from "framer-motion";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowUpRight, Send, Sparkles } from "lucide-react";

const BUDGETS = ["< $3k", "$3k – $8k", "$8k – $20k", "$20k+"];
const PROJECTS = ["Website", "Product UI/UX", "Brand", "Design system", "Other"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: "Website",
    budget: "$3k – $8k",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message sent. I'll be in touch within 24 hours.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-5">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">06 / Contact</p>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-white text-balance">
              Let's make something worth
              <br />
              <span className="italic text-indigo-400">talking about</span>.
            </h2>
            <p className="mt-8 max-w-md text-base text-zinc-400 leading-relaxed">
              Send a short brief. I'll reply within 24 hours with next steps or a polite pass — either way, you'll hear back.
            </p>

            <div className="mt-12 space-y-4">
              <a
                href="mailto:HeroWebDesign@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-white hover:text-indigo-300 transition group"
              >
                <span className="font-display text-lg md:text-xl">HeroWebDesign@gmail.com</span>
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
              </a>
              <a
                href="tel:+15097747789"
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-white hover:text-indigo-300 transition group"
              >
                <span className="font-display text-lg md:text-xl">(509) 774-7789</span>
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
              </a>
              <a
                href="https://instagram.com/herowebdesigns"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="flex items-center gap-3 text-white hover:text-indigo-300 transition group"
              >
                <span className="font-display text-lg md:text-xl">@herowebdesigns</span>
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
              </a>
              <p className="text-sm text-zinc-500 font-mono pt-2">Response time · under 24h · Mon–Fri</p>
            </div>

            <a
              href="https://calendly.com/alixwilson112009/new-meeting"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-call"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white px-5 py-3 text-sm font-medium transition active:scale-[0.98] ai-glow"
            >
              <Sparkles size={16} />
              Book a free 15-min call
            </a>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="contact-form"
            onSubmit={submit}
            className="md:col-span-7 space-y-10"
          >
            {sent ? (
              <div
                data-testid="contact-success"
                className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-10 text-center"
              >
                <div className="font-display text-2xl md:text-3xl text-white">Message received.</div>
                <p className="mt-4 text-zinc-400">
                  Thanks {form.name.split(" ")[0]} — I'll reply to <span className="text-white">{form.email}</span> within 24 hours.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", email: "", project_type: "Website", budget: "$3k – $8k", message: "" });
                  }}
                  className="mt-6 text-sm font-mono uppercase tracking-widest text-indigo-300 hover:text-white transition"
                >
                  Send another →
                </button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">Your name</label>
                  <input
                    data-testid="contact-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Ada Lovelace"
                    className="mt-3 w-full bg-transparent border-b border-white/15 py-3 text-lg md:text-2xl text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-400 transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">Email</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="ada@company.com"
                    className="mt-3 w-full bg-transparent border-b border-white/15 py-3 text-lg md:text-2xl text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-400 transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">Project type</label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {PROJECTS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        data-testid={`contact-project-${p.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                        onClick={() => set("project_type", p)}
                        className={`rounded-full px-4 py-2 text-sm border transition ${
                          form.project_type === p
                            ? "border-white bg-white text-black"
                            : "border-white/15 bg-white/5 text-zinc-300 hover:bg-white/10"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">Budget</label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {BUDGETS.map((b) => (
                      <button
                        key={b}
                        type="button"
                        data-testid={`contact-budget-${b.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                        onClick={() => set("budget", b)}
                        className={`rounded-full px-4 py-2 text-sm border transition ${
                          form.budget === b
                            ? "border-white bg-white text-black"
                            : "border-white/15 bg-white/5 text-zinc-300 hover:bg-white/10"
                        }`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500">Tell me about it</label>
                  <textarea
                    data-testid="contact-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="What are you building, who's it for, and when do you need it?"
                    className="mt-3 w-full bg-transparent border-b border-white/15 py-3 text-lg text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-400 transition resize-none"
                  />
                </div>

                <div className="flex items-center justify-between pt-4">
                  <p className="text-xs text-zinc-500 max-w-xs">
                    I read every message personally. No auto-replies.
                  </p>
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="rounded-full bg-white text-black hover:bg-zinc-200 h-12 px-6"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Send message <Send size={14} className="ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
